import React from 'react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useGetStats } from './queries/statsQueries';
import { SportsResponse } from '../common/store/types';
import DataGrid from '../common/Table/newTable';

interface OwnProps {
  readonly selectedSport: SportsResponse;
  readonly tableHeight?: string;
  readonly backgroundColor?: string;
  readonly emptyText?: string;
}

const weatherColumns: GridColDef[] = [
  { field: 'name', headerName: 'Player', width: 180, flex: 1.2 },
  {
    field: 'dkSalary',
    headerName: 'Salary',
    width: 90,
    flex: 0.7,
    type: 'number',
    renderCell: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : '-'
  },
  {
    field: 'sgWeather',
    headerName: 'SG Weather',
    width: 110,
    flex: 0.8,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(3) : '-'
  },
  {
    field: 'r1TeeTime',
    headerName: 'R1 Tee',
    width: 110,
    flex: 0.8,
    type: 'string',
    renderCell: (params) => params.value || '-'
  },
  {
    field: 'r2TeeTime',
    headerName: 'R2 Tee',
    width: 110,
    flex: 0.8,
    type: 'string',
    renderCell: (params) => params.value || '-'
  },
  {
    field: 'r1AvgWindSpeed',
    headerName: 'R1 Wind',
    width: 85,
    flex: 0.6,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
  {
    field: 'r2AvgWindSpeed',
    headerName: 'R2 Wind',
    width: 85,
    flex: 0.6,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
  {
    field: 'r1StrokesGained',
    headerName: 'R1 SG',
    width: 90,
    flex: 0.7,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(3) : '-'
  },
  {
    field: 'r2StrokesGained',
    headerName: 'R2 SG',
    width: 90,
    flex: 0.7,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(3) : '-'
  },
];

// Convert columns for mobile view - remove flex and use fixed widths
const getMobileColumns = (columns: GridColDef[]): GridColDef[] => {
  return columns.map(col => ({
    ...col,
    flex: undefined,
    width: col.width || 80,
    minWidth: col.width || 80,
  }));
};

const Weather = (props: OwnProps) => {
  const { selectedSport, tableHeight, backgroundColor, emptyText } = props;

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isPGA = selectedSport?.sport === 'PGA';
  const { data: weatherData, isLoading } = useGetStats(
    'PGA',
    'weather',
    isPGA
  );

  // Use mobile-optimized columns on mobile devices
  const columns = isMobile ? getMobileColumns(weatherColumns) : weatherColumns;

  // Add unique row ID based on name
  const getRowId = (row: any) => `${row.name}`;

  // Transform data to include id field for DataGrid
  const rows = React.useMemo(() => {
    return (weatherData || []).map((row: any, index: number) => ({
      ...row,
      id: index + 1,
      nameAndId: getRowId(row),
    }));
  }, [weatherData]);

  return (
    <Box height={tableHeight || '100%'}>
      <DataGrid
        lineups={rows}
        headers={columns}
        isLoading={isLoading}
        selectedView="weather"
        simType="dataHub"
        includeStacks={false}
        setStackView={() => {}}
        tierlocked={false}
        setPlayerList={() => {}}
        setStackList={() => {}}
        pagination
        rowHeight={isMobile ? 52 : 44}
        fontSize={isMobile ? 12 : 14}
        emptyText={emptyText || 'Weather and tee time data is only available for PGA.'}
        backgroundColor={backgroundColor || 'rgba(125, 125, 125, .25)'}
        isMobile={isMobile}
        pinnedColumnsLeft={isMobile ? ['name'] : undefined}
      />
    </Box>
  );
};

export default Weather;
